import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { SAINT_NICK_USER_ID } from '@/utils/constants';
import { MapPin, User, Calendar, Snowflake, Ghost, AlertCircle, Plus, Search, ChevronLeft, ChevronRight, Eye, Edit } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { DisplayEditor } from './DisplayEditor';

interface Display {
  id: number;
  name: string;
  description: string | null;
  location: string;
  city: string | null;
  state: string | null;
  holiday_type: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
  user_profile?: {
    display_name: string;
    full_name: string;
  };
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  created_at: string;
}

import { JACK_SKELLINGTON_USER_ID } from '@/utils/constants';

const ITEMS_PER_PAGE = 10;

interface DisplaysManagementProps {
  preselectedDisplayId?: string | null;
}

const DisplaysManagement: React.FC<DisplaysManagementProps> = ({ preselectedDisplayId }) => {
  const [displays, setDisplays] = useState<Display[]>([]);
  const [filteredDisplays, setFilteredDisplays] = useState<Display[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDisplay, setSelectedDisplay] = useState<Display | null>(null);
  const [newUserId, setNewUserId] = useState<string>('');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHolidayType, setFilterHolidayType] = useState<string>('all');
  const [filterOwnership, setFilterOwnership] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchDisplays();
    fetchUsers();
  }, []);

  // If a display ID is provided (from /admin?display=ID), open editor preselected
  useEffect(() => {
    if (preselectedDisplayId && displays.length > 0) {
      const match = displays.find(d => String(d.id) === String(preselectedDisplayId));
      if (match) {
        // Scroll to editor and noop — the DisplayEditor itself will accept preselection
      }
    }
  }, [preselectedDisplayId, displays]);

  useEffect(() => {
    filterAndSearchDisplays();
  }, [displays, searchTerm, filterHolidayType, filterOwnership]);

  const fetchDisplays = async () => {
    try {
      const { data, error } = await supabase
        .from('displays')
        .select(`
          id,
          name,
          description,
          location,
          city,
          state,
          holiday_type,
          user_id,
          created_at,
          updated_at,
          user_profile:user_profiles(display_name, full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDisplays(data || []);
    } catch (error) {
      console.error('Error fetching displays:', error);
      toast.error('Failed to fetch displays');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Try to fetch from user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, full_name, created_at')
        .order('display_name');

      if (error) {
        console.error('Error fetching from user_profiles:', error);
        // If we can't fetch users, still provide the system accounts
        const systemAccounts = [
          {
            id: SAINT_NICK_USER_ID,
            email: 'placeholder_saintnick@towb.in',
            full_name: 'Saint Nick',
            created_at: '2024-01-01T00:00:00.000Z'
          },
          {
            id: JACK_SKELLINGTON_USER_ID,
            email: 'placeholder_jackskellington@towb.in',
            full_name: 'Jack Skellington',
            created_at: '2024-01-01T00:00:00.000Z'
          }
        ];
        setUsers(systemAccounts);
        toast.error('Could not load full user list. System accounts are available for assignment.');
      } else {
        // Transform the data to match our interface (user_id -> id)
        const transformedData = (data || []).map(user => ({
          id: user.user_id || '',
          email: `user_${user.user_id}@example.com`, // Placeholder since we don't have email in user_profiles
          full_name: user.display_name || user.full_name || 'Unknown',
          created_at: user.created_at || new Date().toISOString()
        }));
        
        // Always include system accounts at the top of the list
        const systemAccounts = [
          {
            id: SAINT_NICK_USER_ID,
            email: 'placeholder_saintnick@towb.in',
            full_name: 'Saint Nick',
            created_at: '2024-01-01T00:00:00.000Z'
          },
          {
            id: JACK_SKELLINGTON_USER_ID,
            email: 'placeholder_jackskellington@towb.in',
            full_name: 'Jack Skellington',
            created_at: '2024-01-01T00:00:00.000Z'
          }
        ];
        
        // Combine system accounts with fetched users, removing duplicates
        const allUsers = [...systemAccounts];
        transformedData.forEach(user => {
          if (user.id !== SAINT_NICK_USER_ID && user.id !== JACK_SKELLINGTON_USER_ID) {
            allUsers.push(user);
          }
        });
        
        setUsers(allUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
      // Set empty users array to prevent crashes
      setUsers([]);
    }
  };

  const filterAndSearchDisplays = () => {
    let filtered = [...displays];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(display => 
        display.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        display.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        display.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        display.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        display.user_profile?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        display.user_profile?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Holiday type filter
    if (filterHolidayType !== 'all') {
      filtered = filtered.filter(display => display.holiday_type === filterHolidayType);
    }

    // Ownership filter
    if (filterOwnership === 'unassigned') {
      filtered = filtered.filter(display => !display.user_id);
    } else if (filterOwnership === 'assigned') {
      filtered = filtered.filter(display => display.user_id);
    } else if (filterOwnership === 'placeholder') {
      filtered = filtered.filter(display => 
        display.user_id === SAINT_NICK_USER_ID || 
        display.user_id === JACK_SKELLINGTON_USER_ID
      );
    }

    setFilteredDisplays(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleOpenAssignDialog = (display: Display) => {
    setSelectedDisplay(display);
    setNewUserId(display.user_id || '');
    setShowAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setShowAssignDialog(false);
    setSelectedDisplay(null);
    setNewUserId('');
  };

  const assignDisplayToUser = async (displayId: number, userId: string) => {
    try {
      // Handle "none" value to remove owner
      const actualUserId = userId === 'none' ? null : userId;
      
      console.log('Assigning display:', { displayId, userId, actualUserId });
      
      const { data, error } = await supabase
        .from('displays')
        .update({ user_id: actualUserId })
        .eq('id', displayId)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update successful:', data);
      toast.success('Display owner updated successfully');
      await fetchDisplays(); // Await the fetch to ensure data is refreshed
      handleCloseAssignDialog();
    } catch (error) {
      console.error('Error updating display owner:', error);
      toast.error(`Failed to update display owner: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const createPlaceholderUser = async () => {
    try {
      // Generate placeholder email if not provided
      const email = newUserEmail || `placeholder_${newUserName.toLowerCase().replace(/\s+/g, '')}@towb.in`;

      const { data, error } = await supabase.auth.admin.createUser({
        email,
        password: Math.random().toString(36).slice(-12), // Random password
        email_confirm: true,
        user_metadata: {
          full_name: newUserName
        }
      });

      if (error) throw error;

      toast.success(`Created user: ${newUserName}`);
      fetchUsers();
      setShowCreateUser(false);
      setNewUserEmail('');
      setNewUserName('');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    }
  };

  const assignToDefaultProfile = async (display: Display) => {
    let targetUserId = SAINT_NICK_USER_ID;
    
    if (display.holiday_type === 'halloween') {
      targetUserId = JACK_SKELLINGTON_USER_ID;
    }

    await assignDisplayToUser(display.id, targetUserId);
  };

  const getDisplayName = (display: Display) => {
    // If the name looks like a UDID (contains dashes and hexadecimal characters)
    const udidPattern = /^User Display [a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    
    if (udidPattern.test(display.name)) {
      // If we have a description, use it
      if (display.description && display.description.trim()) {
        return display.description;
      }
      // Otherwise, create a friendly name based on location and holiday type
      const locationPart = display.city || 'Unknown Location';
      const typePart = display.holiday_type ? 
        display.holiday_type.charAt(0).toUpperCase() + display.holiday_type.slice(1) : 
        'Holiday';
      return `${typePart} Display in ${locationPart}`;
    }
    
    // If the name looks normal, use it as is
    return display.name;
  };

  const getHolidayIcon = (holidayType: string | null) => {
    switch (holidayType) {
      case 'christmas':
        return <Snowflake className="h-4 w-4 text-blue-500" />;
      case 'halloween':
        return <Ghost className="h-4 w-4 text-orange-500" />;
      default:
        return <Calendar className="h-4 w-4 text-gray-500" />;
    }
  };

  const paginatedDisplays = filteredDisplays.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredDisplays.length / ITEMS_PER_PAGE);

  if (loading) {
    return <div className="text-center py-8">Loading displays...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Displays</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displays.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Unassigned</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {displays.filter(d => !d.user_id).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Christmas Displays</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {displays.filter(d => d.holiday_type === 'christmas').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Halloween Displays</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {displays.filter(d => d.holiday_type === 'halloween').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Display Editor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Display Editor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DisplayEditor preselectedDisplayId={preselectedDisplayId || undefined} />
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Display Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search displays..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="holiday-filter">Holiday Type</Label>
              <Select value={filterHolidayType} onValueChange={setFilterHolidayType}>
                <SelectTrigger id="holiday-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="christmas">Christmas</SelectItem>
                  <SelectItem value="halloween">Halloween</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ownership-filter">Ownership</Label>
              <Select value={filterOwnership} onValueChange={setFilterOwnership}>
                <SelectTrigger id="ownership-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Displays</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="placeholder">Placeholder Accounts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Placeholder User</DialogTitle>
                    <DialogDescription>
                      Create a new user account with a placeholder email address.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="new-user-name">Full Name</Label>
                      <Input
                        id="new-user-name"
                        value={newUserName}
                        onChange={(e) => setNewUserName(e.target.value)}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-user-email">Email (optional)</Label>
                      <Input
                        id="new-user-email"
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        placeholder="Leave blank for auto-generated"
                      />
                      {!newUserEmail && newUserName && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Will use: placeholder_{newUserName.toLowerCase().replace(/\s+/g, '')}@towb.in
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={createPlaceholderUser}
                      disabled={!newUserName}
                      className="w-full"
                    >
                      Create User
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Displays Table */}
      <Card>
        <CardHeader>
          <CardTitle>Displays ({filteredDisplays.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedDisplays.map((display) => (
                  <TableRow key={display.id}>
                    <TableCell className="font-medium">{getDisplayName(display)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        {display.city || 'Unknown Location'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {getHolidayIcon(display.holiday_type)}
                        <span className="capitalize">{display.holiday_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {display.user_profile ? (
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{display.user_profile.display_name || display.user_profile.full_name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-orange-600">
                          <AlertCircle className="h-4 w-4" />
                          <span>Unassigned</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(display.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/display/${display.id}`, '_blank')}
                          title="View Display"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => (window.location.href = `/admin?display=${display.id}`)}
                          title="Edit in Admin"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenAssignDialog(display)}
                        >
                          Assign Owner
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Assign Owner Dialog */}
          <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Display Owner</DialogTitle>
                <DialogDescription>
                  Select a user to assign as the owner of "{selectedDisplay ? getDisplayName(selectedDisplay) : ''}"
                </DialogDescription>
              </DialogHeader>
              {selectedDisplay && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="user-select">Select User</Label>
                    <Select
                      value={newUserId}
                      onValueChange={setNewUserId}
                    >
                      <SelectTrigger id="user-select">
                        <SelectValue placeholder="Choose a user" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Owner</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.full_name} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!selectedDisplay.user_id && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Quick assign to default profile based on holiday type:
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => assignDisplayToUser(selectedDisplay.id, newUserId)}
                      disabled={!newUserId || newUserId === selectedDisplay.user_id}
                      className="flex-1"
                    >
                      Assign User
                    </Button>
                    {!selectedDisplay.user_id && (
                      <Button
                        variant="outline"
                        onClick={() => assignToDefaultProfile(selectedDisplay)}
                        className="flex-1"
                      >
                        Assign to Default
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to{' '}
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredDisplays.length)} of{' '}
                {filteredDisplays.length} displays
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DisplaysManagement;